import {
  Component,
  ViewChild,
  OnInit,
  OnDestroy,
  HostListener,
} from "@angular/core";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatSort, MatSortModule, Sort } from "@angular/material/sort";
import { MatDialog } from "@angular/material/dialog";
import { FilterPopupComponent } from "src/app/shared/components/filter-popup/filter-popup.component";
import { Subject, Subscription, takeUntil } from "rxjs";
import { DashboardService } from "src/app/shared/services/dashboard.service";
import { UserService } from "src/app/shared/services/user.service";
import { AppConfigService } from "src/app/services/app-config.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import { ComparisonNormalizer } from "src/app/shared/utils/comparison-normalizer.util";
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { CommonModule } from "@angular/common";
import { MmpMainHeaderComponent } from "../mmp-main-header/mmp-main-header.component";

@Component({
  selector: "app-mmp-eval-dashboard",
  templateUrl: "./mmp-eval-dashboard.component.html",
  styleUrls: ["./mmp-eval-dashboard.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    MmpMainHeaderComponent,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    FormsModule,
  ],
})
export class MmpEvalDashboardComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly arizePopupWidth = 250;
  private readonly arizePopupMaxHeight = 300;
  private readonly arizePopupOffset = 6;
  private readonly arizePopupViewportPadding = 12;
  private readonly arizePopupHeaderHeight = 44;
  private readonly arizePopupRowHeight = 36;
  private readonly arizePopupListPadding = 10;
  activeArizeMenuId: string | null = null;
  selectedArizeProspect: any = null;

  arizePopupPosition = {
    left: 0,
    top: 0,
  };
  displayedColumns: string[] = [
    "prospect",
    "case_number",
    "program",
    "no_of_transcripts",
    "opportunity_start_date",
    "advisor_name",
    "date_assigned",
    "status",
    "salesforce_last_modified_date",
    "tlo_status",
    "trace_last_modified_date",
    "actions",
  ];

  dataSource = new MatTableDataSource<any>([]);

  pageIndex = 0; // zero-based
  pageSize = 10;
  total = 0;
  searchText = "";
  isComfortable = false;

  private allData: any[] = [];
  private filteredSortedData: any[] = [];

  sortingColumn: string | null = "date_assigned";
  sortingOrder: "asc" | "desc" | "" = "desc";

  filterData = {
    program: [],
    status: ["All"],
    tloStatus: ["All"],
    transcripts: ["All"],
    startDate: {
      startDate: null as Date | null,
      endDate: null as Date | null,
    },
    assignedDate: {
      startDate: null as Date | null,
      endDate: null as Date | null,
    },
  };
  filterSelected = {
    program: [],
    status: "All",
    tloStatus: "All",
    transcripts: "All",
    startDate: {
      startDate: null,
      endDate: null,
    },
    assignedDate: {
      startDate: null,
      endDate: null,
    },
  };
  selectedWidget: string = "total-prospects";
  statusCounts: { [key: string]: number } = {};
  lastSyncTime: string = "";
  formattedLastSyncTime: string = "";
  currentUserName: string = "";
  currentUserDnumber: string = "";
  isLoading: boolean = true;
  showConsoleLogs: boolean = false;
  private sortInitialized = false;
  private filtersLoaded = false;
  private previousPageSize: number = 10;
  private effectivePageSize: number = 10;
  private readonly easternTimeFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  // Reactive control for search with validation
  searchControl = new FormControl<string>("", [Validators.maxLength(200)]);

  private searchSub?: Subscription;
  ComparisonNormalizer = ComparisonNormalizer;

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    if (!sort) return;

    const isSameSortInstance = this.sort === sort;
    this.sort = sort;

    //Restore UI state WITHOUT triggering API.
    if (this.sortingColumn && this.sortingOrder) {
      this.sort.active = this.sortingColumn;
      this.sort.direction = this.sortingOrder;

      //Force UI refresh (this is the missing piece)
      (this.sort as any)._stateChanges.next();
    }

    if (isSameSortInstance && this.sortInitialized) {
      return;
    }

    this.sortInitialized = true;

    sort.sortChange
      .pipe(debounceTime(50), takeUntil(this.destroy$))
      .subscribe((sortState: Sort) => {
        if (sortState.direction) {
          this.sortingColumn = sortState.active;
          this.sortingOrder = sortState.direction;
        } else {
          this.sortingColumn = null;
          this.sortingOrder = null;
        }

        this.pageIndex = 0;
        this.fetchData();
      });
  }

  sort!: MatSort;

  constructor(
    private readonly dialog: MatDialog,
    private readonly dashboardService: DashboardService,
    private readonly userService: UserService,
    private readonly envService: AppConfigService,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
  ) {
    this.showConsoleLogs = this.envService.showConsoleLogs;
  }

  ngOnInit(): void {
    const currentUser = this.userService.getUser();
    this.currentUserName = currentUser.name;
    this.currentUserDnumber = currentUser.dnumber;

    // Try to restore previous dashboard state
    const savedState = this.dashboardService.getDashboardState();
    if (savedState) {
      this.pageIndex = savedState.pageIndex;
      this.pageSize = savedState.pageSize;
      this.sortingColumn = savedState.sortingColumn;
      this.sortingOrder = savedState.sortingOrder;
      this.searchText = savedState.searchText;
      this.filterSelected = savedState.filterSelected;
      this.selectedWidget = savedState.selectedWidget;

      // Initialize reactive control with restored value without triggering search
      this.searchControl.setValue(this.searchText, { emitEvent: false });
    }

    this.fetchData();

    // Subscribe to value changes with debounce + distinct
    this.searchSub = this.searchControl.valueChanges
      .pipe(
        map((v) => (v ?? "").trim()),
        debounceTime(600),
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        if (this.searchControl.invalid) {
          this.searchText = "";
          return;
        }
        this.onSearchChange(value);
      });
  }

  private normalizeStatus(rawStatus: any): string {
    if (typeof rawStatus !== "string") return rawStatus;

    const normalized = rawStatus.trim().toLowerCase();

    switch (normalized) {
      case "re-open":
        return "Reopen";
      case "new (admissions)":
        return "New";
      case "pending external feedback":
        return "Pending";
      case "open (admissions)":
        return "Open";
      case "request incomplete response":
        return "Incomplete";
      case "closed":
        return "Closed";
      default:
        return "Others";
    }
  }

  private denormalizeStatus(uiStatus: string): string {
    if (!uiStatus) return uiStatus;

    const normalized = uiStatus.trim().toLowerCase();

    switch (normalized) {
      case "reopen":
        return "Re-Open";
      case "new":
        return "New (Admissions)";
      case "pending":
        return "Pending External Feedback";
      case "open":
        return "Open (Admissions)";
      case "incomplete":
        return "Request Incomplete Response";
      case "closed":
        return "Closed";
      default:
        return uiStatus;
    }
  }

  getTloStatusClass(status?: string | null): string {
    if (!status) {
      return "";
    }

    return status.toLowerCase().trim().replaceAll(/\s+/g, "-");
  }

  getSalesforceStatusClass(status?: string | null): string {
    if (!status) {
      return "others";
    }

    const normalized = status.trim().toLowerCase();
    const knownStatuses = [
      "new",
      "reopen",
      "incomplete",
      "closed",
      "pending",
      "open",
    ];

    return knownStatuses.includes(normalized) ? normalized : "others";
  }

  normalizeProgramFilter(): string | string[] {
    if (Array.isArray(this.filterSelected.program)) {
      return this.filterSelected.program.map((p) =>
        ComparisonNormalizer.string(p) === "all" ? "" : p,
      );
    }

    const isAllProgram =
      ComparisonNormalizer.string(this.filterSelected.program) === "all";
    return isAllProgram ? "" : this.filterSelected.program;
  }

  fetchData(): void {
    this.isLoading = true;

    // Normalize filters for payload: replace "All" with "" as required
    const programFilter = this.normalizeProgramFilter();

    const statusFilter =
      ComparisonNormalizer.string(this.filterSelected.status) === "all"
        ? ""
        : this.filterSelected.status;

    const tloStatusFilter =
      ComparisonNormalizer.string(this.filterSelected.tloStatus) === "all"
        ? ""
        : this.filterSelected.tloStatus;

    const transcriptsFilter =
      ComparisonNormalizer.string(this.filterSelected.transcripts) === "all"
        ? ""
        : this.filterSelected.transcripts;

    const payload = {
      user_dnumber: this.currentUserDnumber,
      limit: this.pageSize,
      page: this.pageIndex + 1,
      sorting_column: this.sortingColumn,
      sorting_order: this.sortingOrder,
      search_string: this.searchText,
      filters: {
        program: programFilter,
        status: this.denormalizeStatus(statusFilter),
        tloStatus: tloStatusFilter,
        no_of_transcripts: transcriptsFilter,
        date_assigned: {
          start: this.formatDateOnly(
            this.filterSelected.assignedDate.startDate,
          ),
          end: this.formatDateOnly(this.filterSelected.assignedDate.endDate),
        },
        opportunity_start_date: {
          start: this.formatDateOnly(this.filterSelected.startDate.startDate),
          end: this.formatDateOnly(this.filterSelected.startDate.endDate),
        },
      },
    };

    //fetch dashboard details
    this.dashboardService
      .fetchDashboardDetails(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (raw: any) => {
          // Ensure data is always an array
          const dataArray = Array.isArray(raw?.data) ? raw.data : [];

          this.allData = dataArray.map((item: any) => ({
            ...item,
            status: this.normalizeStatus(item?.status ?? ""),
          }));

          // Ensure total_count is a number
          this.total =
            typeof raw?.total_count === "number" ? raw.total_count : 0;

          this.lastSyncTime = raw?.last_sync_date ?? "";
          this.formattedLastSyncTime = this.formatLastSyncTime(
            this.lastSyncTime,
          );

          // Ensure status_analytics is a plain object
          this.statusCounts =
            raw?.status_analytics && typeof raw.status_analytics === "object"
              ? raw.status_analytics
              : {};

          if (this.showConsoleLogs) {
            console.log("Dashboard API parsed data:", {
              allData: this.allData,
              total: this.total,
              statusCounts: this.statusCounts,
              lastSyncTime: this.lastSyncTime,
              formattedLastSyncTime: this.formattedLastSyncTime,
            });
          }

          this.loadData();
        },
        error: (error) => {
          if (this.showConsoleLogs) {
            console.error("Error fetching dashboard details:", error);
          }
          this.showToast(
            "toast-error",
            "Error fetching dashboard details, please refresh the page.",
          );
        },
      });
  }

  private formatLastSyncTime(rawTimestamp: string): string {
    if (ComparisonNormalizer.string(rawTimestamp) === "") {
      return "";
    }

    const parsedDate = new Date(rawTimestamp);
    if (Number.isNaN(parsedDate.getTime())) {
      return "";
    }

    const parts = this.easternTimeFormatter.formatToParts(parsedDate);
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    const year = parts.find((part) => part.type === "year")?.value;
    const hour = parts.find((part) => part.type === "hour")?.value;
    const minute = parts.find((part) => part.type === "minute")?.value;
    const dayPeriod = parts.find((part) => part.type === "dayPeriod")?.value;

    if (!month || !day || !year || !hour || !minute || !dayPeriod) {
      return "";
    }

    return `${month}/${day}/${year} at ${hour}:${minute} ${dayPeriod}`;
  }

  loadData(): void {
    let data = [...this.allData];

    this.filteredSortedData = data;

    this.dataSource.data = data;

    this.updateDisplayedColumns();
    this.updateWidget();

    this.isLoading = false;
  }

  private formatDateOnly(date: Date | null): string | null {
    if (!date) return null;

    // Normalize to remove time portion
    const normalized = this.normalizeDate(new Date(date));
    // Return yyyy-mm-dd
    const year = normalized.getFullYear();
    const month = String(normalized.getMonth() + 1).padStart(2, "0"); // 0-based
    const day = String(normalized.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  onSearchChange(value: string): void {
    this.searchText = value.trim();
    this.pageIndex = 0;
    this.fetchData();
  }

  updateDisplayedColumns() {
    const baseColumns = [
      "prospect",
      "case_number",
      "program",
      "no_of_transcripts",
      "opportunity_start_date",
      "advisor_name",
      "date_assigned",
    ];

    if (ComparisonNormalizer.string(this.filterSelected?.status) === "closed") {
      this.displayedColumns = [
        ...baseColumns,
        "salesforce_last_modified_date",
        "completedDate",
        "actions",
      ];
    } else {
      this.displayedColumns = [
        ...baseColumns,
        "status",
        "salesforce_last_modified_date",
        "tlo_status",
        "trace_last_modified_date",
        "actions",
      ];
    }
  }

  updateWidget() {
    const statusWidgetMap: any = {
      All: "total-prospects",
      New: "new",
      Reopen: "reopen",
      Open: "open",
      Pending: "pending",
      Incomplete: "incomplete",
      Closed: "closed",
    };

    this.selectedWidget =
      statusWidgetMap[this.filterSelected?.status] || "total-prospects";
  }

  get totalPages(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  changePage(page: number): void {
    if (page < 0 || page >= this.totalPages || page === this.pageIndex) {
      return;
    }

    this.pageIndex = page;
    this.fetchData();
  }

  onPageSizeChange(): void {
    this.pageIndex = 0;
    this.fetchData();
  }

  get visiblePages(): number[] {
    if (ComparisonNormalizer.number(this.totalPages) === 0) return [];

    const current = this.pageIndex + 1;

    if (this.totalPages <= 3) {
      return Array.from({ length: this.totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3];
    }

    if (current >= this.totalPages - 1) {
      return [this.totalPages - 2, this.totalPages - 1, this.totalPages];
    }

    return [current - 1, current, current + 1];
  }

  toggleSpacing(): void {
    this.isComfortable = !this.isComfortable;
  }

  viewDetails(row: any): void {
    if (!row?.case_number || !row?.brand_profile_id) {
      return;
    }
    // Save current table state in service
    this.dashboardService.setDashboardState({
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      sortingColumn: this.sortingColumn,
      sortingOrder: this.sortingOrder,
      searchText: this.searchText,
      filterSelected: this.filterSelected,
      selectedWidget: this.selectedWidget,
    });

    // Current user's combinedId
    const currentCombinedId = `${row.case_number}_${row.brand_profile_id}`;

    this.router.navigate(["/user-transcripts", currentCombinedId], {
      state: {
        origin: "dashboard",
      },
    });
  }

  openFilterDialog(): void {
    // If filters already loaded once, just open dialog with cached filterData
    if (this.filtersLoaded) {
      this.openFilterDialogWithData();
      return;
    }

    // Lazy-load filters only when user first opens the filter dialog
    this.dashboardService
      .fetchDashboardFilters()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (filterResponse) => {
          if (this.showConsoleLogs) {
            console.log("Filter API response:", filterResponse);
          }

          // Safely normalize response fields (make sure to adapt keys to your API)
          const normalizeArray = (value: any): string[] => {
            if (!Array.isArray(value)) return [];
            return value
              .filter((v) => v !== null && v !== undefined && v !== "")
              .map(String);
          };

          const programs = normalizeArray(filterResponse?.programs);
          const statuses = normalizeArray(filterResponse?.status);
          const tloStatuses = normalizeArray(filterResponse?.tlo_status);
          const transcripts = normalizeArray(filterResponse?.no_of_transcripts);

          this.filterData = {
            program: [...programs],
            status: ["All", ...statuses],
            tloStatus: ["All", ...tloStatuses],
            transcripts: ["All", ...transcripts],
            startDate: {
              startDate: null,
              endDate: null,
            },
            assignedDate: {
              startDate: null,
              endDate: null,
            },
          };

          this.filtersLoaded = true;

          // Open dialog ONLY after filterData is updated
          this.openFilterDialogWithData();
        },
        error: (error) => {
          if (this.showConsoleLogs) {
            console.error("Error fetching filters:", error);
          }
          this.showToast(
            "toast-error",
            "Error fetching filters. Using default filter values.",
          );
          // Fallback: still open dialog with whatever filterData we have (default)
          this.openFilterDialogWithData();
        },
      });
  }

  openFilterDialogWithData() {
    const dialogRef = this.dialog.open(FilterPopupComponent, {
      width: "48%",
      panelClass: "filter-dialog-container",
      data: this.filterData,
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.filterSelected = result.data;
          if (ComparisonNormalizer.string(result.action) === "apply") {
            this.pageIndex = 0;
            this.fetchData();
          }
        }
      });
  }

  private normalizeDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  hasActiveFilters(): boolean {
    if (!this.filterSelected) return false;

    const hasProgramFilter =
      Array.isArray(this.filterSelected.program) &&
      this.filterSelected.program.some((p) => p !== "");

    return !!(
      hasProgramFilter ||
      (this.filterSelected.tloStatus &&
        ComparisonNormalizer.string(this.filterSelected.tloStatus) !== "all") ||
      (this.filterSelected.transcripts &&
        ComparisonNormalizer.string(this.filterSelected.transcripts) !==
          "all") ||
      (this.filterSelected.startDate &&
        (this.filterSelected.startDate.startDate ||
          this.filterSelected.startDate.endDate)) ||
      (this.filterSelected.assignedDate &&
        (this.filterSelected.assignedDate.startDate ||
          this.filterSelected.assignedDate.endDate))
    );
  }

  /**
   * Clears all applied filters and reloads the data
   */
  clearFilters(): void {
    this.filterSelected = {
      program: [],
      status: "All",
      tloStatus: "All",
      transcripts: "All",
      startDate: {
        startDate: null,
        endDate: null,
      },
      assignedDate: {
        startDate: null,
        endDate: null,
      },
    };

    // Reset page index to first page
    this.pageIndex = 0;

    // Reload the data with cleared filters
    this.fetchData();

    this.dashboardService.updateFilter(this.filterSelected);
  }

  setActiveWidget(widget: string): void {
    this.selectedWidget = widget;

    const statusMap: { [key: string]: string } = {
      "total-prospects": "All",
      new: "New",
      reopen: "Reopen",
      open: "Open",
      pending: "Pending",
      incomplete: "Incomplete",
      closed: "Closed",
    };

    this.filterSelected = {
      ...this.filterSelected,
      status: statusMap[widget] || "All",
    };

    this.pageIndex = 0;
    this.fetchData();
    this.dashboardService.updateFilter(this.filterSelected);
  }

  Math = Math;

  private getArizeMenuId(prospect: any): string {
    const caseNumber = ComparisonNormalizer.string(prospect?.case_number);
    const brandProfileId = ComparisonNormalizer.string(
      prospect?.brand_profile_id,
    );
    const fallbackId = ComparisonNormalizer.string(prospect?.prospect);

    return `${caseNumber}_${brandProfileId}_${fallbackId}`;
  }

  getArizeLinks(prospect: any): Array<{ url: string; label: string }> {
    const links = Array.isArray(prospect?.arize_url) ? prospect.arize_url : [];

    return links
      .map((item: any, index: number) => {
        const url = item?.url || "";
        const transcriptName = ComparisonNormalizer.string(
          item?.transcript_name,
        ).trim();

        return {
          url,
          label: transcriptName || `Transcript ${index + 1}`,
        };
      })
      .filter((item) => item.url !== "");
  }

  hasArizeLinks(prospect: any): boolean {
    return this.getArizeLinks(prospect).length > 0;
  }

  toggleArizeMenu(event: Event, prospect: any): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.hasArizeLinks(prospect)) {
      return;
    }

    const menuId = this.getArizeMenuId(prospect);

    // Close if the same menu is clicked again
    if (this.activeArizeMenuId === menuId) {
      this.closeArizeMenu();
      return;
    }

    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const buttonRight = Number.isFinite(rect.right) ? rect.right : rect.left;
    const preferredLeft = buttonRight - this.arizePopupWidth;
    const maxLeft =
      viewportWidth - this.arizePopupWidth - this.arizePopupViewportPadding;
    const left = Math.max(
      this.arizePopupViewportPadding,
      Math.min(preferredLeft, maxLeft),
    );

    const estimatedPopupHeight = Math.min(
      this.arizePopupMaxHeight,
      this.arizePopupHeaderHeight +
        this.arizePopupListPadding +
        this.getArizeLinks(prospect).length * this.arizePopupRowHeight,
    );
    const hasEnoughSpaceBelow =
      viewportHeight - rect.bottom >=
      estimatedPopupHeight + this.arizePopupViewportPadding;
    const preferredTop = hasEnoughSpaceBelow
      ? rect.bottom + this.arizePopupOffset
      : rect.top - estimatedPopupHeight - this.arizePopupOffset;
    const maxTop = viewportHeight - this.arizePopupViewportPadding;
    const top = Math.max(
      this.arizePopupViewportPadding,
      Math.min(preferredTop, maxTop),
    );

    this.activeArizeMenuId = menuId;
    this.selectedArizeProspect = prospect;

    // Since popup will use position: fixed
    this.arizePopupPosition = {
      left,
      top,
    };
  }

  closeArizeMenu(): void {
    this.activeArizeMenuId = null;
    this.selectedArizeProspect = null;
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    // If no popup is open, nothing to do
    if (!this.activeArizeMenuId) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) {
      this.closeArizeMenu();
      return;
    }

    // If the click is inside the popup or the Arize button, do NOT close
    const isInsidePopup = !!target.closest(".arize-popup");
    const isOnArizeButton = !!target.closest(".arize-btn");

    if (!isInsidePopup && !isOnArizeButton) {
      this.closeArizeMenu();
    }
  }

  showToast(type: string, message: string) {
    this.snackBar.open(message, "x", {
      duration: 3000,
      horizontalPosition: "right",
      verticalPosition: "top",
      panelClass: [type],
    });
  }

  get skeletonRows(): number[] {
    if (this.previousPageSize === this.pageSize) {
      this.effectivePageSize = this.pageSize + 1;
    } else {
      this.effectivePageSize = Math.max(this.previousPageSize, this.pageSize);
      this.previousPageSize = this.pageSize + 1;
    }

    // Creates an array [0, 1, 2, ..., pageSize-1]
    return Array.from({ length: this.effectivePageSize + 1 }, (_, i) => i);
  }

  ngOnDestroy(): void {
    // Emit a value to complete all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clear large data structures
    this.allData = [];
    this.dataSource.data = [];

    this.searchSub?.unsubscribe();
  }
}
